import { ReactElement } from 'react'
import { NavLink } from 'react-router-dom'
import { Formik, getIn, useFormikContext } from 'formik'
import { lowerCase } from 'lodash'
import styled from 'styled-components'
import { useIntl, FormattedMessage, defineMessages } from 'react-intl'
import { Checkbox } from 'components/v2/Checkbox'
import { Select } from 'components/v2/Select'
import { Button } from 'components/v2/Button'
import { Input } from 'components/Input'
import { Label } from 'components/v2/Label'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import { InputPercentage } from 'components/InputPercentage'
import { InputNumber } from 'components/InputNumber'
import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import { FilePreview, InputFile } from 'components/v2/InputFile'
import FormLayout from 'features/App/components/FormLayout'
import {
  GTCampaignType,
  GTConfirmationModality,
  GTCouponConfirmationModalities,
  GTDurationUnit
} from 'gql/types.generated'
import { SelectSalesChannelsV2 } from 'features/Services/components/SelectSalesChannelsV2'
import { confirmationModalitiesMessages } from 'features/Services/components/ConfirmationModalities/messages'
import { getUnprocessableErrors } from 'utils/error'
import { safeFormatMessage } from 'utils/intl'
import { EConfirmationModality } from 'features/Bookings/types'
import { schema } from './schema'
import { INewCouponTemplateForm, INewCouponTemplateFormProps, INewCouponTemplateProps } from './types'
import { CONFIRMATION_MODALITIES_LIST, DURATION_UNIT_LIST } from 'features/Campaigns/constants'
import { useMutation } from '@apollo/client'
import { VALIDATE_COUPONS_CSV } from 'features/Campaigns/mutations-legacy'
import { usePushFileToS3 } from 'utils/file'
import { ISalesChannel } from 'features/Services/components/NewServicesV2/components/OfferSettingsCard/types'

const ExpirationTimeWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 20px;
`
const CheckboxWrapper = styled.div`
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`
const StyledLabel = styled(Label)`
  margin-bottom: 10px;
`
const messages = defineMessages({
  DAYS: {
    id: 'coupons.form.expirationUnits.days',
    defaultMessage: 'Days'
  },
  HOURS: {
    id: 'coupons.form.expirationUnits.hours',
    defaultMessage: 'Hours'
  },
  expirationTimePlaceholder: {
    id: 'coupons.form.expirationTime.placeholder',
    defaultMessage: 'Time'
  },
  expirationUnitsPlaceholder: {
    id: 'coupons.form.expirationUnits.placeholder',
    defaultMessage: 'Units'
  },
  salesChannelsPlaceholder: {
    id: 'salesChannels.autocomplete.placeholder',
    defaultMessage: 'Select sales channels'
  },
  prefixPlaceholder: {
    id: 'coupon.template.prefix.placeholder',
    defaultMessage: 'Code'
  },
  subcampaignNamePlaceholder: {
    id: 'coupon.template.subcampaignName.placeholder',
    defaultMessage: 'Subcampaign name'
  }
})

const NewCouponTemplateForm = ({
  error,
  loading,
  isEdit,
  canCreateDBSubcampaign,
  canModifyTemplate
}: INewCouponTemplateFormProps): ReactElement => {
  const { values, touched, errors, setFieldValue, handleBlur, handleSubmit, setFieldError, setFieldTouched } =
    useFormikContext<INewCouponTemplateForm>()
  const pushFileToS3 = usePushFileToS3()
  const [validateCSV] = useMutation(VALIDATE_COUPONS_CSV)
  const intl = useIntl()

  const handleSalesChannelsChange = (salesChannels: ISalesChannel[]): void => {
    setFieldValue('salesChannels', salesChannels)
  }
  const handleConfirmationModalitiesChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let confirmationModalities = values.confirmationModalities
    const value = e.target.id as GTConfirmationModality
    if (!confirmationModalities.includes(value)) {
      confirmationModalities = [...confirmationModalities, value]
    } else {
      confirmationModalities = confirmationModalities.filter((confirmationModality) => confirmationModality !== value)
    }
    setFieldValue('confirmationModalities', confirmationModalities)
  }
  const uploadFile = async (file: File): Promise<string | undefined> => {
    try {
      const path = await pushFileToS3(file)
      return path
    } catch (error) {
      await setFieldTouched('file', true)
      setFieldError('file', 'Error uploading file. Try again')
    }
  }
  const handleFileChange = async (file: File | ''): Promise<void> => {
    if (!file) {
      await setFieldTouched('file', false)
      await setFieldValue('file', null)
      setFieldValue('filePath', '')
      return
    }
    try {
      const path = await uploadFile(file)
      if (path) {
        const res = await validateCSV({
          variables: {
            input: {
              path
            }
          }
        })
        const isValidFile = res.data
        if (isValidFile) {
          await setFieldValue('filePath', path)
          await setFieldValue('file', file)
        }
      }
    } catch (error) {
      await setFieldTouched('file', true)
      setFieldError('file', 'Data in the file does not have the right format. Try again')
    }
  }

  return (
    <form autoComplete="off" onSubmit={handleSubmit}>
      <FormLayout.CardContent>
        <Row>
          <Column width="50%">
            <Input
              id="prefix"
              onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>): void => {
                setFieldValue('prefix', value.trim().toUpperCase())
              }}
              onBlur={handleBlur}
              value={values.prefix}
              placeholder={intl.formatMessage(messages.prefixPlaceholder)}
              touched={touched.prefix}
              errors={errors.prefix}
              label={<FormattedMessage id="coupon.template.prefix.label" defaultMessage="Prefix (optional)" />}
              readOnly={isEdit}
              disabled
            />
          </Column>
          <Column width="50%">
            <Label>
              <FormattedMessage id="dictionary.campaign" defaultMessage="Campaign" />
            </Label>
            {values.campaign && values.campaign.id && <Input value={values.campaign.name} readOnly />}
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <SelectSalesChannelsV2
              id="salesChannels"
              values={values.salesChannels}
              placeholder={intl.formatMessage(messages.salesChannelsPlaceholder)}
              onChange={handleSalesChannelsChange}
              touched={getIn(touched, 'salesChannels')}
              errors={getIn(errors, 'salesChannels')}
              label={<FormattedMessage id="dictionary.salesChannels" defaultMessage="Sales channels" />}
              disabled={!canModifyTemplate}
            />
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <label>
              <Label>
                <FormattedMessage id="dictionary.value" defaultMessage="Percentage Discount" />
              </Label>
              <InputPercentage
                id="value"
                value={values.value}
                placeholder="0%"
                onChange={(value: string): void => {
                  setFieldValue('value', value)
                }}
                onBlur={handleBlur}
                touched={touched.value}
                errors={errors.value}
                disabled={!canModifyTemplate}
              />
            </label>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <StyledLabel>
              <FormattedMessage id="dictionary.expirationTime" defaultMessage="Expiration time" />
            </StyledLabel>
            <ExpirationTimeWrapper>
              <InputNumber
                id="expirationTime"
                onChange={(value: string): void => {
                  setFieldValue('expirationTime.value', value)
                }}
                onBlur={handleBlur}
                value={values.expirationTime.value ?? ''}
                placeholder={intl.formatMessage(messages.expirationTimePlaceholder)}
                touched={touched.expirationTime?.value || touched.expirationTime?.unit}
                errors={errors.expirationTime?.value || errors.expirationTime?.unit}
                disabled={!canModifyTemplate}
              />
              <Select
                id="expirationTime.unit"
                onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
                  setFieldValue('expirationTime.unit', e.target.value)
                }}
                value={values.expirationTime.unit}
                disabled={!canModifyTemplate}
              >
                {DURATION_UNIT_LIST.map((unit) => {
                  return (
                    <option value={unit} key={unit}>
                      {safeFormatMessage({ intl, messages, key: unit })}
                    </option>
                  )
                })}
              </Select>
            </ExpirationTimeWrapper>
          </Column>
        </Row>
        <Row>
          <Column width="50%">
            <StyledLabel>
              <FormattedMessage
                id="coupons.form.confirmationModalities.label"
                defaultMessage="Confirmation modalities"
              />
            </StyledLabel>
            <div>
              {CONFIRMATION_MODALITIES_LIST.map((confirmationModality: GTConfirmationModality): ReactElement => {
                return (
                  <CheckboxWrapper key={confirmationModality}>
                    <Checkbox
                      id={confirmationModality}
                      checked={values.confirmationModalities.includes(confirmationModality)}
                      onChange={handleConfirmationModalitiesChange}
                      disabled={!canModifyTemplate}
                    >
                      {confirmationModalitiesMessages[lowerCase(confirmationModality) as EConfirmationModality]
                        ? safeFormatMessage({
                            intl,
                            key: lowerCase(confirmationModality),
                            messages: confirmationModalitiesMessages
                          })
                        : confirmationModality}
                    </Checkbox>
                  </CheckboxWrapper>
                )
              })}
              {touched.confirmationModalities && errors.confirmationModalities && (
                <ErrorMessage>{errors.confirmationModalities}</ErrorMessage>
              )}
            </div>
          </Column>
        </Row>
        {values.campaign.type === GTCampaignType.UniqueCouponForDatabase && canCreateDBSubcampaign && (
          <>
            <Row>
              <Column width="50%">
                <Input
                  id="subcampaignName"
                  onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>): void => {
                    setFieldValue('subcampaignName', value)
                  }}
                  onBlur={handleBlur}
                  value={values.subcampaignName}
                  placeholder={intl.formatMessage(messages.subcampaignNamePlaceholder)}
                  touched={touched.subcampaignName}
                  errors={errors.subcampaignName}
                  label={
                    <FormattedMessage id="coupon.template.subcampaignName.label" defaultMessage="Subcampaign Name" />
                  }
                />
              </Column>
            </Row>
            <Row>
              <Column width="50%">
                <InputFile
                  accept={{
                    'text/csv': []
                  }}
                  value={values.file}
                  label={<FormattedMessage id="coupons.templates.form.uploadFile" defaultMessage="CSV FILE" />}
                  onChange={handleFileChange}
                  touched={touched.file}
                  errors={errors.file}
                />
              </Column>
            </Row>
            {values.file && (
              <Row>
                <Column width="50%">
                  <FilePreview
                    file={values.file}
                    onRemoveFile={(): void => {
                      handleFileChange('')
                    }}
                  />
                </Column>
              </Row>
            )}
          </>
        )}
      </FormLayout.CardContent>
      <FormLayout.Split />
      <FormLayout.CardFooter>
        {error && (
          <div>
            <ErrorMessage errors={getUnprocessableErrors(error)}>
              <FormattedMessage
                id="coupons.templates.form.error"
                defaultMessage="There was an error while creating your template"
              />
            </ErrorMessage>
          </div>
        )}
        <FormLayout.CardFooterActions>
          <Button as={NavLink} to="/campaigns" kind="cancel">
            <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
          </Button>
          <Button type="submit" kind="primary" loading={loading}>
            {isEdit ? (
              <FormattedMessage id="dictionary.update" defaultMessage="Update" />
            ) : (
              <FormattedMessage id="dictionary.create" defaultMessage="Create" />
            )}
          </Button>
        </FormLayout.CardFooterActions>
      </FormLayout.CardFooter>
    </form>
  )
}

export const NewCouponTemplate = ({
  campaign,
  onSubmit,
  error,
  loading,
  isEdit,
  canCreateDBSubcampaign,
  canModifyTemplate
}: INewCouponTemplateProps): ReactElement => {
  const intl = useIntl()

  const initialCoupon: INewCouponTemplateForm = {
    file: null,
    filePath: '',
    subcampaignName: '',
    prefix: campaign?.template?.prefix || '',
    campaign: {
      id: campaign.id || '',
      name: campaign.name || '',
      type: campaign.type || ''
    },
    value: campaign.template?.value ? String(campaign.template.value) : '',
    salesChannels: campaign.template?.salesChannelsV2 || [],
    expirationTime: campaign.template?.expirationTime
      ? {
          value: String(campaign.template.expirationTime.value),
          unit: campaign.template.expirationTime.unit
        }
      : {
          value: '',
          unit: GTDurationUnit.Days
        },
    confirmationModalities: CONFIRMATION_MODALITIES_LIST.filter(
      (modality: GTConfirmationModality) =>
        campaign.template?.confirmationModalities[lowerCase(modality) as keyof GTCouponConfirmationModalities] === true
    )
  }

  return (
    <Formik initialValues={initialCoupon} validationSchema={schema(intl, canCreateDBSubcampaign)} onSubmit={onSubmit}>
      {(props): ReactElement => {
        return (
          <NewCouponTemplateForm
            {...props}
            isEdit={isEdit}
            error={error}
            loading={loading}
            campaign={campaign}
            canCreateDBSubcampaign={canCreateDBSubcampaign}
            canModifyTemplate={canModifyTemplate}
          />
        )
      }}
    </Formik>
  )
}
